import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import { ArrowRight, Filter, UserPlus, Users } from "lucide-react";
import SalesAffiliateLayout from "../components/SalesAffiliateLayout";
import SalesAffiliateStatusBanner from "../components/SalesAffiliateStatusBanner";
import SalesAffiliateLeadRegistrationForm from "../components/SalesAffiliateLeadRegistrationForm";
import SalesAffiliateLeadRow from "../components/SalesAffiliateLeadRow";
import { CompactCommissionSnapshot } from "../components/SalesAffiliateCommercialSummary";
import {
  ExecutiveBriefing,
  ExecutiveHero,
  ExecutiveSnapshot,
  ExecutiveTimeline,
  ProspectPipeline,
} from "../components/SalesAffiliateExecutiveWorkspace";
import { useSalesAffiliateWorkspace } from "../context/SalesAffiliateWorkspaceContext";
import { supabase } from "../lib/supabaseClient";
import { getSalesAffiliateFunctionErrorMessage, salesAffiliateFunctionCodeMessage } from "../lib/salesAffiliateCommercial";

const LEAD_FUNCTION = "sales_affiliate_lead_registration";
const LEAD_SELECT_COLUMNS =
  "id, first_name, last_name, company_name, email, phone, status, claimed_at, withdrawn_at, created_at, converted_at, converted_transaction_type";

const WITHDRAWN_STATUSES = new Set(["WITHDRAWN"]);
const CONVERTED_STATUSES = new Set(["CONVERTED"]);
const DUPLICATE_STATUSES = new Set(["REJECTED_DUPLICATE"]);
const INVALID_STATUSES = new Set(["REJECTED_INVALID"]);

const VIEW_OPTIONS = [
  { value: "ACTIVE", label: "Active Protected Prospects" },
  { value: "ALL", label: "All Prospect Records" },
  { value: "CONVERTED", label: "Converted Customers" },
  { value: "DUPLICATE", label: "Duplicate Attempts" },
  { value: "INVALID", label: "Not Accepted" },
  { value: "WITHDRAWN", label: "Withdrawn" },
];

function normalizeStatus(value) {
  return String(value || "REGISTERED")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function formatStatus(value) {
  return normalizeStatus(value)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatDate(value) {
  if (!value) return "Date unavailable";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsedDate);
}

function firstToken(value) {
  if (typeof value !== "string") return "";
  return value.trim().split(/\s+/)[0] || "";
}

function getFirstName(status) {
  const directCandidates = [
    status?.first_name,
    status?.affiliate_first_name,
    status?.applicant_first_name,
    status?.profile?.first_name,
    status?.application?.first_name,
    status?.affiliate_application?.first_name,
  ];
  const directFirstName = directCandidates.find(
    (candidate) =>
      typeof candidate === "string" && candidate.trim().length > 0,
  );
  if (directFirstName) return directFirstName.trim();

  const fullNameCandidates = [
    status?.full_name,
    status?.display_name,
    status?.profile?.full_name,
    status?.application?.full_name,
    status?.affiliate_application?.full_name,
  ];
  const fullName = fullNameCandidates.find(
    (candidate) =>
      typeof candidate === "string" && candidate.trim().length > 0,
  );
  return firstToken(fullName);
}

function getAuthFirstName(user) {
  const metadata = user?.user_metadata || {};
  const directCandidates = [
    metadata.first_name,
    metadata.given_name,
  ];
  const directFirstName = directCandidates.find(
    (candidate) =>
      typeof candidate === "string" && candidate.trim().length > 0,
  );
  if (directFirstName) return directFirstName.trim();

  return firstToken(metadata.full_name || metadata.name || "");
}

function LoadingState() {
  return (
    <div className="flex min-h-[420px] items-center justify-center">
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 shadow-sm">
        Loading dashboard…
      </div>
    </div>
  );
}

function leadMatchesView(lead, view) {
  const status = normalizeStatus(lead.status);

  switch (view) {
    case "ALL":
      return true;
    case "CONVERTED":
      return CONVERTED_STATUSES.has(status);
    case "DUPLICATE":
      return DUPLICATE_STATUSES.has(status);
    case "INVALID":
      return INVALID_STATUSES.has(status);
    case "WITHDRAWN":
      return WITHDRAWN_STATUSES.has(status);
    case "ACTIVE":
    default:
      // Fail closed to the certified current backend lifecycle: only ACCEPTED
      // is an active Protected Prospect.
      return status === "ACCEPTED";
  }
}

export default function SalesAffiliateDashboard() {
  const {
    loading,
    statusLoadFailed,
    errorMessage,
    workspaceData: status,
    configuration,
    lifecycleState,
    refreshWorkspace,
  } = useSalesAffiliateWorkspace();

  const registrationSectionRef = useRef(null);
  const prospectsSectionRef = useRef(null);

  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsError, setLeadsError] = useState("");
  const [prospectView, setProspectView] = useState("ACTIVE");
  const [authenticatedFirstName, setAuthenticatedFirstName] = useState("");

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    setLeadsError("");

    try {
      const { data, error } = await supabase
        .from("sales_affiliate_leads")
        .select(LEAD_SELECT_COLUMNS)
        .order("created_at", { ascending: false });

      if (error) throw new Error("Unable to load your prospect records right now. Please try again.");
      setLeads(data || []);
    } catch (err) {
      setLeadsError(err?.message || "Unable to load your prospect records right now. Please try again.");
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && !statusLoadFailed && configuration.allowsDashboardPage) {
      loadLeads();
    }
  }, [
    loading,
    statusLoadFailed,
    configuration.allowsDashboardPage,
    loadLeads,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadAuthenticatedFirstName = async () => {
      const { data } = await supabase.auth.getUser();
      if (!cancelled) {
        setAuthenticatedFirstName(getAuthFirstName(data?.user));
      }
    };

    loadAuthenticatedFirstName();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleWithdraw = async (leadId) => {
    const { data, error } = await supabase.functions.invoke(LEAD_FUNCTION, {
      body: { action: "withdraw", lead_id: leadId },
    });

    if (error) {
      throw new Error(
        await getSalesAffiliateFunctionErrorMessage(
          error,
          "Unable to withdraw this protected prospect right now. Please try again.",
        ),
      );
    }
    if (!data?.ok) {
      throw new Error(
        salesAffiliateFunctionCodeMessage(
          data?.error,
          "Unable to withdraw this protected prospect right now. Please try again.",
        ),
      );
    }
    await loadLeads();
  };

  const scrollToRegistration = () => {
    registrationSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const scrollToProspects = () => {
    prospectsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const dashboardMetrics = useMemo(() => {
    const totalLeads = leads.length;
    const withdrawnLeads = leads.filter((lead) =>
      WITHDRAWN_STATUSES.has(normalizeStatus(lead.status)),
    );
    const convertedLeads = leads.filter((lead) =>
      CONVERTED_STATUSES.has(normalizeStatus(lead.status)),
    );
    const activeLeads = leads.filter(
      (lead) => normalizeStatus(lead.status) === "ACCEPTED",
    );
    const invalidLeads = leads.filter((lead) =>
      INVALID_STATUSES.has(normalizeStatus(lead.status)),
    );
    const duplicateLeads = leads.filter((lead) =>
      DUPLICATE_STATUSES.has(normalizeStatus(lead.status)),
    );
    const priorityProspect = activeLeads[0] || null;
    const awaitingFollowUpLeads = activeLeads;
    const activeProtectedProspectCount = activeLeads.length;
    const convertedProspectCount = convertedLeads.length;
    const protectedPortfolioCount =
      activeProtectedProspectCount + convertedProspectCount;
    const conversionRate =
      protectedPortfolioCount > 0
        ? Math.round((convertedProspectCount / protectedPortfolioCount) * 100)
        : null;

    return {
      totalLeads,
      activeLeads,
      registeredLeads: activeLeads,
      engagedLeads: [],
      convertedLeads,
      withdrawnLeads,
      invalidLeads,
      duplicateLeads,
      priorityProspect,
      awaitingFollowUpLeads,
      protectedProspectCount: protectedPortfolioCount,
      convertedProspectCount,
      conversionRate,
    };
  }, [leads]);

  const executiveGuidance = useMemo(() => {
    const {
      activeLeads,
      registeredLeads,
      priorityProspect,
      protectedProspectCount,
      convertedProspectCount,
      conversionRate,
    } = dashboardMetrics;

    const secondaryPriorities = activeLeads
      .filter((prospect) => prospect.id !== priorityProspect?.id)
      .slice(0, 2)
      .map((prospect) => ({
        label: `Follow up with ${prospect.company_name || "a protected prospect"}`,
        action: scrollToProspects,
      }));

    const conversionSummary =
      protectedProspectCount === 0
        ? "No Protected Prospects Yet"
        : `${convertedProspectCount} of ${protectedProspectCount} Protected ${protectedProspectCount === 1 ? "Prospect" : "Prospects"} Converted`;
    const conversionCard = {
      title: conversionRate === null ? "—" : `${conversionRate}%`,
      summary: conversionSummary,
      actionLabel: "View protected prospects",
      action: scrollToProspects,
    };

    if (activeLeads.length === 0) {
      const hasConvertedCustomers = convertedProspectCount > 0;
      return {
        priority: {
          title: hasConvertedCustomers
            ? "Register your next prospect"
            : "Register your first prospect",
          reasons: [
            "No active prospect is currently protected",
            "Registration establishes first-accepted ownership",
            hasConvertedCustomers
              ? "Your converted Customers remain recorded separately while you build the next opportunity"
              : "A focused first prospect creates immediate forward momentum",
          ],
          recommendation:
            "Start with a business where you already have a credible relationship and a clear reason to discuss AI regulatory exposure.",
          actionLabel: "+ Add New Prospect",
          action: scrollToRegistration,
        },
        additionalPriorities: [],
        conversion: conversionCard,
        growth: {
          title: hasConvertedCustomers
            ? "Build the next prospect cycle"
            : "Create a focused prospect list",
          summary:
            "Identify organizations using AI in consequential business processes, then register the strongest fit.",
          actionLabel: hasConvertedCustomers ? "Add next prospect" : "Add first prospect",
          action: scrollToRegistration,
        },
      };
    }


    if (registeredLeads.length > 0) {
      const prospect = priorityProspect;
      return {
        priority: {
          title: `Follow up with ${prospect?.company_name || "your newest prospect"}`,
          reasons: [
            "The prospect is protected but not yet showing a later-stage status",
            "Early follow-up helps prevent the registration from becoming stale",
            "A concrete next step is the best available progress signal",
          ],
          recommendation:
            "Make a focused follow-up that establishes interest, identifies the decision-maker, and confirms the next action.",
          actionLabel: "Review prospect",
          action: scrollToProspects,
        },
        additionalPriorities: secondaryPriorities,
        conversion: conversionCard,
        growth: {
          title: "Increase conversion momentum",
          summary: `Contact ${prospect?.company_name || "your leading prospect"} this week to maximize conversion probability.`,
          actionLabel: "Review prospect",
          action: scrollToProspects,
        },
      };
    }

    return {
      priority: {
        title: "Replenish your protected portfolio",
        reasons: [
          "No currently active protected prospect is available to prioritize",
          "A new registration restores forward momentum",
          "Consistent prospecting supports long-term affiliate growth",
        ],
        recommendation:
          "Begin the next cycle now: identify, register, engage, advance, and replenish.",
        actionLabel: "+ Add New Prospect",
        action: scrollToRegistration,
      },
      additionalPriorities: [],
      conversion: conversionCard,
      growth: {
        title: "Repeat the strongest behavior",
        summary:
          "Sustainable growth comes from a consistent cycle of focused prospecting and disciplined follow-up.",
        actionLabel: "Build portfolio",
        action: scrollToRegistration,
      },
    };
  }, [dashboardMetrics]);

  const recentActivity = useMemo(() => {
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return leads
      .filter((lead) => {
        const normalized = normalizeStatus(lead.status);
        const eventDate = new Date(
          WITHDRAWN_STATUSES.has(normalized)
            ? lead.withdrawn_at || lead.created_at
            : CONVERTED_STATUSES.has(normalized)
              ? lead.converted_at || lead.created_at
              : lead.created_at,
        ).getTime();
        return Number.isFinite(eventDate) && eventDate >= thirtyDaysAgo;
      })
      .slice(0, 4);
  }, [leads]);
  const filteredLeads = useMemo(
    () => leads.filter((lead) => leadMatchesView(lead, prospectView)),
    [leads, prospectView],
  );
  const affiliateFirstName = getFirstName(status) || authenticatedFirstName;

  if (loading) {
    return (
      <SalesAffiliateLayout activeSection="dashboard">
        <LoadingState />
      </SalesAffiliateLayout>
    );
  }

  if (statusLoadFailed) {
    return (
      <SalesAffiliateLayout activeSection="dashboard">
        <div className="mx-auto flex min-h-[500px] max-w-2xl items-center px-8">
          <SalesAffiliateStatusBanner
            tone="warning"
            eyebrow="Workspace unavailable"
            title="We could not load your Sales Affiliate status"
          >
            <p>{errorMessage || "Please retry the status check."}</p>
            <button
              type="button"
              onClick={refreshWorkspace}
              className="mt-5 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-black text-white transition hover:bg-emerald-700"
            >
              Retry status check
            </button>
          </SalesAffiliateStatusBanner>
        </div>
      </SalesAffiliateLayout>
    );
  }

  if (!configuration.allowsDashboardPage) {
    return <Navigate to={configuration.defaultPath} replace />;
  }
  if (!status?.affiliate_exists) {
    return <Navigate to="/sales-affiliate/application" replace />;
  }

  return (
    <SalesAffiliateLayout activeSection="dashboard" lifecycleState={lifecycleState}>
      <main className="mx-auto max-w-7xl px-6 py-7 sm:px-8 lg:py-9">
        <ExecutiveHero
          firstName={affiliateFirstName}
          activeCount={dashboardMetrics.activeLeads.length}
          onReview={scrollToProspects}
          onAddProspect={scrollToRegistration}
        />

        <ExecutiveBriefing guidance={executiveGuidance} />
        <ExecutiveSnapshot metrics={dashboardMetrics} />
        <CompactCommissionSnapshot requirements={status?.requirements || []} />

        <section className="mt-7 grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
          <ProspectPipeline
            metrics={dashboardMetrics}
            onReview={scrollToProspects}
          />
          <ExecutiveTimeline
            leads={recentActivity}
            loading={leadsLoading}
            error={leadsError}
            formatStatus={formatStatus}
            formatDate={formatDate}
          />
        </section>

        <section ref={registrationSectionRef} className="scroll-mt-6 mt-6">
          <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-3xl font-black tracking-tight text-slate-950">
                Register a Prospect
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Register a business to establish first-accepted protection before advancing the commercial conversation.
              </p>
            </div>
          </div>
          <SalesAffiliateLeadRegistrationForm onRegistered={loadLeads} />
        </section>

        <section
          ref={prospectsSectionRef}
          className="scroll-mt-6 mt-7 rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-7 sm:py-7"
        >
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-50 text-teal-700 ring-1 ring-teal-100">
                  <Users size={22} />
                </div>
                <div>
                  <div className="text-[11px] font-black uppercase tracking-[0.24em] text-teal-700">
                    Protected portfolio
                  </div>
                  <h2 className="mt-1 text-3xl font-black tracking-tight text-slate-950">
                    My Protected Prospects
                  </h2>
                </div>
              </div>
              <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600">
                Review your protected prospects, focus on active commercial work, and release protection only when a business should leave your portfolio.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div>
                <label
                  htmlFor="prospect-view"
                  className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500"
                >
                  View
                </label>
                <div className="relative mt-2">
                  <Filter
                    size={15}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-blue-600"
                  />
                  <select
                    id="prospect-view"
                    value={prospectView}
                    onChange={(event) => setProspectView(event.target.value)}
                    className="min-w-[220px] appearance-none rounded-2xl border border-slate-300 bg-white py-3 pl-9 pr-10 text-sm font-black text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    {VIEW_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-blue-700">
                  Showing
                </div>
                <div className="mt-1 text-2xl font-black text-slate-950">
                  {filteredLeads.length}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            {leadsLoading ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-6 text-center text-sm font-semibold text-slate-600">
                Loading your protected prospects…
              </div>
            ) : leadsError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-6">
                <p className="text-sm font-semibold text-red-700">{leadsError}</p>
                <button
                  type="button"
                  onClick={loadLeads}
                  className="mt-4 rounded-2xl bg-emerald-600 px-4 py-2 text-xs font-black text-white shadow-sm transition hover:bg-emerald-700"
                >
                  Retry
                </button>
              </div>
            ) : leads.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-9 text-center">
                <UserPlus className="mx-auto text-blue-600" size={26} />
                <div className="mt-3 text-sm font-black text-slate-900">
                  No protected prospects yet
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Register your first prospect to begin building your protected portfolio.
                </p>
                <button
                  type="button"
                  onClick={scrollToRegistration}
                  className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-xs font-black text-white transition hover:bg-emerald-700"
                >
                  + Add New Prospect
                  <ArrowRight size={15} />
                </button>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-9 text-center">
                <div className="text-sm font-black text-slate-900">
                  No protected prospects match this view
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Choose another view to see the rest of your protected prospect portfolio.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredLeads.map((lead) => (
                  <SalesAffiliateLeadRow
                    key={lead.id}
                    lead={lead}
                    onWithdraw={handleWithdraw}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </SalesAffiliateLayout>
  );
}
